import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, Plus, Search, Trash2, Upload } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';

const Stacked = ({ top, bottom, topBold = true, topColor, bottomColor, mutedColor }) => (
  <div style={{ lineHeight: 1.25 }}>
    <div style={{ fontWeight: topBold ? 700 : 500, fontSize: '12px', color: topColor || '#0f172a' }}>
      {top || '—'}
    </div>
    {bottom ? (
      <div style={{ fontSize: '11px', color: bottomColor || mutedColor || '#64748b', marginTop: '1px' }}>
        {bottom}
      </div>
    ) : null}
  </div>
);

const contactStatusPresentation = (statusRaw, isDarkMode) => {
  const s = String(statusRaw || '').trim().toLowerCase();
  const base = { label: statusRaw && String(statusRaw).trim() ? String(statusRaw).trim() : '—' };
  if (s === 'activo') {
    return {
      ...base,
      bg: isDarkMode ? '#14532d' : '#dcfce7',
      color: isDarkMode ? '#bbf7d0' : '#166534',
      border: isDarkMode ? '#22c55e' : '#86efac',
    };
  }
  if (s === 'inactivo') {
    return {
      ...base,
      bg: isDarkMode ? '#3f3f46' : '#f4f4f5',
      color: isDarkMode ? '#d4d4d8' : '#52525b',
      border: isDarkMode ? '#52525b' : '#d4d4d8',
    };
  }
  return {
    ...base,
    bg: isDarkMode ? '#27272a' : '#f0ebe3',
    color: isDarkMode ? '#e4e4e7' : '#44403c',
    border: isDarkMode ? '#3f3f46' : '#d6d0c4',
  };
};

const TABLE_COLUMNS = [
  { key: 'Código', align: 'left', sortKey: 'contact_code' },
  { key: 'Contacto', align: 'left', sortKey: 'full_name' },
  { key: 'Proveedor', align: 'left', sortKey: 'supplier_name' },
  { key: 'Correo', align: 'left', sortKey: 'email' },
  { key: 'Teléfono', align: 'left', sortKey: 'phone' },
  { key: 'Estado', align: 'center', sortKey: 'status' },
];

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'contact_code':
      return String(row.contact_code || '').toLowerCase();
    case 'full_name':
      return String(row.full_name || '').toLowerCase();
    case 'supplier_name':
      return String(row.supplier_name || '').toLowerCase();
    case 'email':
      return String(row.email || '').toLowerCase();
    case 'phone':
      return String(row.phone || '').toLowerCase();
    case 'status':
      return String(row.status || '').toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

const SupplierContactsTable = ({
  rows = [],
  loading = false,
  error = '',
  isDarkMode = false,
  tableTitle = 'Contactos',
  addButtonLabel = 'Nuevo contacto',
  bulkImportLabel = 'Carga masiva',
  onAdd,
  onBulkImport,
  onBulkImportTemplate,
  onEdit,
  onBulkDelete,
}) => {
  const ui = useUIColors();
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;
  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortColumn, setSortColumn] = useState('full_name');
  const [sortDir, setSortDir] = useState('asc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkPending, setBulkPending] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const headerSelectRef = useRef(null);

  const showBulkChrome = Boolean(onBulkDelete);
  const colCount = showBulkChrome ? TABLE_COLUMNS.length + 1 : TABLE_COLUMNS.length;

  const statusOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const st = String(r.status || '').trim();
      if (st) map.set(st, st);
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows]);

  const filteredRows = useMemo(() => {
    let list = rows;
    const q = searchQuery.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = [
          row.contact_code,
          row.full_name,
          row.role,
          row.email,
          row.phone,
          row.supplier_name,
          row.status,
        ]
          .filter((x) => x !== undefined && x !== null)
          .map((x) => String(x))
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }
    if (filterStatus) {
      list = list.filter((row) => String(row.status || '').trim() === filterStatus);
    }
    return list;
  }, [rows, searchQuery, filterStatus]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => compareRows(a, b, sortColumn, sortDir));
    return arr;
  }, [filteredRows, sortColumn, sortDir]);

  const visibleIds = useMemo(
    () => sortedRows.map((r) => Number(r.id)).filter((id) => Number.isFinite(id)),
    [sortedRows]
  );

  const allVisibleSelected =
    showBulkChrome && visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected = showBulkChrome && visibleIds.some((id) => selectedIds.includes(id));

  useEffect(() => {
    const el = headerSelectRef.current;
    if (!el || el.type !== 'checkbox') return;
    el.indeterminate = someVisibleSelected && !allVisibleSelected;
  }, [someVisibleSelected, allVisibleSelected, showBulkChrome]);

  const toggleSelectOne = (rawId) => {
    const id = Number(rawId);
    if (!Number.isFinite(id)) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      if (visibleIds.length && visibleIds.every((id) => prev.includes(id))) {
        return prev.filter((id) => !visibleIds.includes(id));
      }
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const handleSortClick = (key) => {
    if (sortColumn === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(key);
      setSortDir('asc');
    }
  };

  const hasActiveFilters = Boolean(searchQuery.trim() || filterStatus);
  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
  };

  const handleBulkDelete = async () => {
    if (!onBulkDelete || selectedIds.length === 0) return;
    setBulkPending(true);
    setBulkError('');
    try {
      const result = await onBulkDelete(selectedIds);
      if (result !== false) setSelectedIds([]);
    } catch (err) {
      setBulkError(err?.message || 'No se pudieron eliminar.');
    } finally {
      setBulkPending(false);
    }
  };

  const subtitle = loading
    ? 'Cargando…'
    : error ||
      (hasActiveFilters
        ? `Mostrando ${sortedRows.length} de ${rows.length} registros`
        : `${rows.length} registros`);

  const selectStyle = {
    height: '36px',
    padding: '0 10px',
    borderRadius: '8px',
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: '13px',
    minWidth: '140px',
    cursor: 'pointer',
    outline: 'none',
  };

  const SortIcon = ({ columnKey }) => {
    if (sortColumn !== columnKey) return <ArrowUpDown size={12} color={muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} color={text} /> : <ArrowDown size={12} color={text} />;
  };

  return (
    <div
      style={{
        border: `1px solid ${border}`,
        borderRadius: '12px',
        overflow: 'hidden',
        background: bg,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${border}`,
          background: headerBg,
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: text }}>{tableTitle}</h3>
          <p style={{ margin: 0, fontSize: '12px', color: muted }}>{subtitle}</p>
        </div>
        {(onAdd || onBulkImport) ? (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            {onAdd ? (
              <button
                type="button"
                onClick={onAdd}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  height: '36px',
                  padding: '0 14px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#16a34a',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                <Plus size={16} /> {addButtonLabel}
              </button>
            ) : null}
            {onBulkImport ? (
              <div style={{ display: 'inline-flex', alignItems: 'stretch' }}>
                <button
                  type="button"
                  onClick={onBulkImport}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    height: '36px',
                    padding: '0 12px',
                    borderRadius: onBulkImportTemplate ? '8px 0 0 8px' : '8px',
                    border: `1px solid ${inputBorder}`,
                    borderRight: onBulkImportTemplate ? 'none' : `1px solid ${inputBorder}`,
                    background: isDarkMode ? '#27272a' : '#fff',
                    color: text,
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  <Upload size={16} /> {bulkImportLabel}
                </button>
                {onBulkImportTemplate ? (
                  <button
                    type="button"
                    onClick={onBulkImportTemplate}
                    title="Descargar plantilla Excel"
                    aria-label="Descargar plantilla Excel"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '34px',
                      height: '36px',
                      padding: 0,
                      borderRadius: '0 8px 8px 0',
                      border: `1px solid ${inputBorder}`,
                      background: isDarkMode ? '#3f3f46' : '#f8fafc',
                      color: text,
                      cursor: 'pointer',
                    }}
                  >
                    <ChevronDown size={16} />
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 220px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Buscar
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Nombre, correo, proveedor…"
              style={{ ...selectStyle, flex: 1, minWidth: 0, cursor: 'text' }}
            />
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '140px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Estado
          </span>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={selectStyle}>
            <option value="">Todos</option>
            {statusOptions.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
        </label>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              height: '36px',
              padding: '0 12px',
              borderRadius: '8px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: text,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            Limpiar
          </button>
        ) : null}
      </div>

      {showBulkChrome && selectedIds.length > 0 ? (
        <div
          style={{
            padding: '10px 16px',
            borderBottom: `1px solid ${border}`,
            background: isDarkMode ? '#1c1917' : '#fafaf9',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: '13px', color: text, fontWeight: 600 }}>
            {selectedIds.length} seleccionado{selectedIds.length === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            disabled={bulkPending}
            onClick={handleBulkDelete}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '32px',
              padding: '0 12px',
              borderRadius: '6px',
              border: 'none',
              background: '#dc2626',
              color: '#fff',
              fontWeight: 600,
              fontSize: '12px',
              cursor: bulkPending ? 'wait' : 'pointer',
              opacity: bulkPending ? 0.7 : 1,
            }}
          >
            <Trash2 size={14} /> Eliminar
          </button>
          {bulkError ? <span style={{ fontSize: '12px', color: '#dc2626' }}>{bulkError}</span> : null}
        </div>
      ) : null}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
          <thead>
            <tr style={{ background: headerBg, borderBottom: `1px solid ${border}` }}>
              {showBulkChrome ? (
                <th style={{ padding: `${thPadV}px ${padH}px`, width: 36, textAlign: 'center' }}>
                  <input
                    ref={headerSelectRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    aria-label="Seleccionar todos"
                  />
                </th>
              ) : null}
              {TABLE_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: `${thPadV}px ${padH}px`,
                    textAlign: col.align,
                    fontSize: '11px',
                    fontWeight: 700,
                    color: muted,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={() => handleSortClick(col.sortKey)}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {col.key}
                    <SortIcon columnKey={col.sortKey} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  Cargando contactos…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: '#dc2626' }}>
                  {error}
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  No hay contactos para mostrar.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const statusStyle = contactStatusPresentation(row.status, isDarkMode);
                const selected = selectedIds.includes(Number(row.id));
                return (
                  <tr
                    key={row.id}
                    style={{
                      borderBottom: `1px solid ${border}`,
                      background: selected ? rowHover : bg,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = rowHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = selected ? rowHover : bg;
                    }}
                  >
                    {showBulkChrome ? (
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleSelectOne(row.id)}
                          aria-label={`Seleccionar ${row.full_name || row.id}`}
                        />
                      </td>
                    ) : null}
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: '12px', color: muted }}>
                      <code style={{ fontSize: 12 }}>{row.contact_code || '—'}</code>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px` }}>
                      <button
                        type="button"
                        onClick={() => onEdit?.(row)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          padding: 0,
                          textAlign: 'left',
                          cursor: onEdit ? 'pointer' : 'default',
                          color: text,
                        }}
                      >
                        <Stacked
                          top={row.full_name}
                          bottom={row.role || 'Sin cargo'}
                          topColor={text}
                          mutedColor={muted}
                        />
                      </button>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: '12px', color: text }}>
                      {row.supplier_name || '—'}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: '12px', color: muted }}>
                      {row.email || '—'}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: '12px', color: muted }}>
                      {row.phone || '—'}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          padding: '2px 8px',
                          borderRadius: '999px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: statusStyle.bg,
                          color: statusStyle.color,
                          border: `1px solid ${statusStyle.border}`,
                        }}
                      >
                        {statusStyle.label}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SupplierContactsTable;
